import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Typography, Link as MuiLink } from '@mui/material';

interface MermaidDiagramProps { code: string; index: number }

let mermaidInitialized = false;
let mermaidIdCounter = 0;

function MermaidDiagram({ code, index }: MermaidDiagramProps) {
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');
  const idRef = useRef(`mermaid-${index}-${++mermaidIdCounter}`);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mermaid = (await import('mermaid')).default;
        if (!mermaidInitialized) {
          mermaid.initialize({
            startOnLoad: false,
            theme: 'dark',
            fontFamily: 'Inter, system-ui, sans-serif',
            securityLevel: 'loose',
            themeVariables: {
              background: 'transparent',
              primaryColor: '#1D8296',
              primaryTextColor: '#E4E8EE',
              primaryBorderColor: '#5FC4D8',
              lineColor: '#9CA3AF',
              secondaryColor: '#374151',
              tertiaryColor: '#1F2937',
            },
          });
          mermaidInitialized = true;
        }
        const { svg: rendered } = await mermaid.render(idRef.current, code);
        if (!cancelled) setSvg(rendered);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  if (error) {
    return (
      <Box sx={{ my: 2, p: 2, borderRadius: 1, border: '1px solid var(--pm-error)', background: 'rgba(220, 38, 38, 0.08)' }}>
        <Typography variant="caption" sx={{ color: 'var(--pm-error)', fontWeight: 600, display: 'block', mb: 1 }}>Mermaid-Render-Fehler</Typography>
        <Box component="pre" sx={{ m: 0, fontSize: '0.7rem', color: 'var(--pm-fg-muted)', whiteSpace: 'pre-wrap' }}>{error}</Box>
        <Box component="pre" sx={{ m: 0, mt: 1, fontSize: '0.7rem', color: 'var(--pm-fg-subtle)', whiteSpace: 'pre-wrap' }}>{code}</Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        my: 2.5,
        p: 2,
        borderRadius: 1,
        border: '1px solid var(--pm-bg-muted)',
        background: 'var(--pm-bg-deep)',
        textAlign: 'center',
        overflow: 'auto',
        '& svg': { maxWidth: '100%', height: 'auto' },
      }}
      dangerouslySetInnerHTML={{ __html: svg || '<span style="color:var(--pm-fg-subtle);font-size:0.75rem">Rendering…</span>' }}
    />
  );
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[äöüß]/g, (c) => ({ ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' }[c] ?? c))
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function flattenChildren(node: unknown): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(flattenChildren).join('');
  if (node && typeof node === 'object' && 'props' in node) {
    const props = (node as { props?: { children?: unknown } }).props;
    return flattenChildren(props?.children);
  }
  return '';
}

interface Props {
  content: string;
}

export function MarkdownRenderer({ content }: Props) {
  let mermaidIdx = 0;
  return (
    <Box
      sx={{
        '& h1': { fontSize: '2rem', fontWeight: 700, mb: 2, mt: 0, color: 'var(--pm-fg-base)', borderBottom: '2px solid var(--pm-bg-muted)', pb: 1.5, scrollMarginTop: '80px' },
        '& h2': { fontSize: '1.5rem', fontWeight: 700, mb: 1.5, mt: 4, color: 'var(--pm-fg-base)', scrollMarginTop: '80px' },
        '& h3': { fontSize: '1.2rem', fontWeight: 600, mb: 1, mt: 3, color: 'var(--pm-fg-accent)', scrollMarginTop: '80px' },
        '& h4': { fontSize: '1rem', fontWeight: 600, mb: 0.75, mt: 2, color: 'var(--pm-fg-base)', scrollMarginTop: '80px' },
        '& p': { mb: 1.5, lineHeight: 1.7, color: 'var(--pm-fg-base)' },
        '& a': { color: 'var(--pm-fg-accent)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
        '& ul, & ol': { pl: 3, mb: 1.5 },
        '& li': { mb: 0.5, lineHeight: 1.6 },
        '& blockquote': {
          borderLeft: '4px solid var(--pm-fg-accent)',
          background: 'var(--pm-bg-deep)',
          margin: '1.5rem 0',
          padding: '0.75rem 1.25rem',
          borderRadius: '0 4px 4px 0',
          fontStyle: 'italic',
          '& p': { mb: 0, color: 'var(--pm-fg-muted)' },
        },
        '& code': {
          background: 'var(--pm-bg-deep)',
          padding: '0.15rem 0.4rem',
          borderRadius: '3px',
          fontFamily: '"JetBrains Mono", Menlo, Consolas, monospace',
          fontSize: '0.85em',
          color: 'var(--pm-fg-accent)',
          border: '1px solid var(--pm-bg-muted)',
        },
        '& pre': {
          background: 'var(--pm-bg-deep)',
          color: 'var(--pm-fg-base)',
          padding: 2,
          borderRadius: 1,
          overflowX: 'auto',
          fontSize: '0.8rem',
          lineHeight: 1.5,
          border: '1px solid var(--pm-bg-muted)',
          mb: 2,
          '& code': { background: 'transparent', padding: 0, border: 0, fontSize: 'inherit', color: 'inherit' },
        },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          mb: 2,
          fontSize: '0.85rem',
        },
        '& th': {
          background: 'var(--pm-bg-deep)',
          color: 'var(--pm-fg-subtle)',
          fontWeight: 600,
          textAlign: 'left',
          padding: '0.5rem 0.75rem',
          borderBottom: '2px solid var(--pm-bg-muted)',
        },
        '& td': {
          padding: '0.5rem 0.75rem',
          borderBottom: '1px solid var(--pm-bg-muted)',
          color: 'var(--pm-fg-base)',
        },
        '& hr': { border: 0, borderTop: '1px solid var(--pm-bg-muted)', my: 4 },
        '& img': { maxWidth: '100%', height: 'auto', borderRadius: '4px' },
        '& strong': { color: 'var(--pm-fg-base)', fontWeight: 700 },
        '& em': { color: 'var(--pm-fg-muted)' },
        '& input[type="checkbox"]': { mr: 1 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => <h1 id={slugify(flattenChildren(children))}>{children}</h1>,
          h2: ({ children }) => <h2 id={slugify(flattenChildren(children))}>{children}</h2>,
          h3: ({ children }) => <h3 id={slugify(flattenChildren(children))}>{children}</h3>,
          h4: ({ children }) => <h4 id={slugify(flattenChildren(children))}>{children}</h4>,
          a: ({ href, children }) => {
            const external = href?.startsWith('http');
            return (
              <MuiLink href={href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined} underline="hover">
                {children}
              </MuiLink>
            );
          },
          code: ({ className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const lang = match?.[1];
            const codeText = String(children).replace(/\n$/, '');
            if (lang === 'mermaid') {
              return <MermaidDiagram code={codeText} index={mermaidIdx++} />;
            }
            const inline = !className;
            if (inline) {
              return <code {...props}>{children}</code>;
            }
            return <code className={className} {...props}>{children}</code>;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
}

/**
 * TOC-Eintrag aus Markdown-Headings extrahieren (H1-H3, für Sidebar-Navigation).
 */
export interface TocEntry {
  level: number;
  title: string;
  anchor: string;
}

export function extractToc(markdown: string): TocEntry[] {
  const lines = markdown.split('\n');
  const entries: TocEntry[] = [];
  let inCodeBlock = false;
  for (const line of lines) {
    if (line.startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) continue;
    const match = /^(#{1,3})\s+(.+)$/.exec(line);
    if (match) {
      const level = match[1]!.length;
      const title = match[2]!.trim().replace(/`/g, '');
      entries.push({ level, title, anchor: slugify(title) });
    }
  }
  return entries;
}
